import React from 'react';
import { Designer } from '@graphql/Designer/queries/availableDesigners.graphql';
import { getResizedImageUrl } from '@lib/image';
import analyticsTrack from '@lib/analytics/analyticsTrack';
import DesignerServiceTag from '@components/shared/DesignerServiceTag/DesignerServiceTag';
import fakeRating from './fakeRating';
import Stars from './Stars';
import styles from './FeaturedDesigners.module.scss';

interface IDesignerCardProps {
  designer: Designer;
  // In-person/online tag only shows where service type matters to the choice
  // (the /interior-designers city pages). Off by default so shared surfaces
  // like the homepage keep the original card.
  showServiceTag?: boolean;
}

const PLACEHOLDER_BG = '#eef0f0';

// Card top image renders at 322x202; avatar renders at 104x104. 2x for retina.
function topDesignUrl(designer: Designer): string | null {
  const raw = designer.topDesigns?.[0]?.mainBoardAsset?.asset?.url;
  if (!raw) return null;
  return getResizedImageUrl({
    url: raw,
    width: 644,
    height: 404,
    quality: 80,
  });
}

function avatarUrl(photo: string): string {
  return getResizedImageUrl({
    url: photo,
    width: 208,
    height: 208,
    quality: 80,
  });
}

function profileHref(designer: Designer): string {
  return designer.profileUri || `/interior-designers/${designer.id}`;
}

export default function DesignerCard({ designer, showServiceTag = false }: IDesignerCardProps) {
  const top = topDesignUrl(designer);
  const href = profileHref(designer);
  const rating = fakeRating(designer.id);

  return (
    <article className={styles.Card}>
      <div className={styles.CardImage} style={{ background: PLACEHOLDER_BG }}>
        {top && (
          <img
            src={top}
            alt={`${designer.firstName}'s design work`}
            loading="lazy"
            draggable={false}
          />
        )}
      </div>
      <div className={styles.CardAvatar}>
        <img
          src={avatarUrl(designer.photo)}
          alt={`${designer.firstName} ${designer.lastName}`}
          loading="lazy"
          draggable={false}
        />
      </div>
      <div className={styles.CardBody}>
        <h3 className={styles.CardName}>{designer.firstName}</h3>
        {designer.location && (
          <p className={styles.CardLocation}>{designer.location}</p>
        )}
        {showServiceTag && (
          <DesignerServiceTag
            isInPerson={designer.isInPerson}
            className={styles.CardServiceTag}
          />
        )}
        <Stars rating={rating} />
        <a
          href={href}
          className={styles.CardLink}
          draggable={false}
          onClick={() => {
            analyticsTrack('home_featured_designer_click', {
              designer_id: designer.id,
              designer_name: designer.firstName,
            });
          }}
        >
          Learn More
        </a>
      </div>
    </article>
  );
}
